import { dateFormatter } from "@api/date";
import { loanApi } from "@api/loans";
import { recordToPidType } from "@api/utils";
import { SeeAllButton } from "@components/backoffice/buttons/SeeAllButton";
import { Error } from "@components/Error";
import { Loader } from "@components/Loader";
import { ResultsTable } from "@components/ResultsTable/ResultsTable";
import { invenioConfig } from "@config";
import { BackOfficeRoutes } from "@routes/urls";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { Link } from "react-router-dom";
import { Button, Header, Message, Segment } from "semantic-ui-react";
import { checkBorrowingLimit } from "@utils/CirculationUtils";

export default class ItemPendingLoans extends Component {
  constructor(props) {
    super(props);
    this.state = {
      patronsWithOverdue: new Set(), // Track patrons with overdue loans
      patronsAtLimit: new Set(), // Track patrons who reached borrowing limit
      patronStats: new Map(), // Store borrowing stats for each patron
      checkingOverdue: false,
      checkingLimits: false,
    };
  }

  componentDidMount() {
    const {
      itemDetails: { metadata },
      fetchPendingLoans,
    } = this.props;
    fetchPendingLoans(metadata.document_pid);
  }

  componentDidUpdate(prevProps) {
    // Check for overdue loans and borrowing limits when pending loans data is loaded/updated
    if (
      prevProps.data !== this.props.data &&
      this.props.data &&
      this.props.data.hits &&
      this.props.data.hits.length > 0 &&
      !this.state.checkingOverdue &&
      !this.state.checkingLimits
    ) {
      this.checkForOverdueLoans();
      this.checkForBorrowingLimits();
    }
  }

  checkForOverdueLoans = async () => {
    const { data } = this.props;

    if (!data || !data.hits || data.hits.length === 0) return;

    this.setState({ checkingOverdue: true });
    const patronsWithOverdue = new Set();

    const patronPids = [
      ...new Set(data.hits.map((hit) => hit.metadata.patron_pid)),
    ];

    for (const patronPid of patronPids) {
      try {
        const overdueQuery = loanApi
          .query()
          .withPatronPid(patronPid)
          .overdue()
          .withState(["ITEM_ON_LOAN"]) // Only check currently active loans
          .withSize(1)
          .qs();

        const response = await loanApi.list(overdueQuery);
        if (response.data && response.data.total > 0) {
          patronsWithOverdue.add(patronPid);
        }
      } catch (error) {
        console.error(
          `Error checking overdue loans for patron ${patronPid}:`,
          error
        );
      }
    }

    this.setState({
      patronsWithOverdue,
      checkingOverdue: false,
    });
  };

  checkForBorrowingLimits = async () => {
    const { data } = this.props;

    if (!data || !data.hits || data.hits.length === 0) return;

    this.setState({ checkingLimits: true });
    const patronsAtLimit = new Set();
    const patronStats = new Map();

    const patronPids = [
      ...new Set(data.hits.map((hit) => hit.metadata.patron_pid)),
    ];

    for (const patronPid of patronPids) {
      try {
        const borrowingStats = await checkBorrowingLimit(patronPid);

        patronStats.set(patronPid, borrowingStats);

        if (!borrowingStats.canBorrow) {
          patronsAtLimit.add(patronPid);
        }
      } catch (error) {
        patronStats.set(patronPid, {
          current: 0,
          limit: 0,
          canBorrow: true,
        });
      }
    }

    this.setState({
      patronsAtLimit,
      patronStats,
      checkingLimits: false,
    });
  };

  seeAllButton = () => {
    const {
      itemDetails: { metadata },
    } = this.props;
    const path = BackOfficeRoutes.loansListWithQuery(
      loanApi
        .query()
        .withDocPid(metadata.document_pid)
        .withState(invenioConfig.CIRCULATION.loanRequestStates)
        .qs()
    );
    return <SeeAllButton to={path} />;
  };

  patronDetails = ({ row }) => {
    const { patronsWithOverdue, patronsAtLimit, patronStats } = this.state;
    const patronPid = row.metadata.patron_pid;
    const patronHasOverdue = patronsWithOverdue.has(patronPid);
    const patronAtLimit = patronsAtLimit.has(patronPid);
    const stats = patronStats.get(patronPid);

    return (
      <div>
        <Link to={BackOfficeRoutes.patronDetailsFor(patronPid)}>
          {row.metadata.patron.name}
        </Link>
        <div style={{ marginTop: "4px" }}>
          {patronHasOverdue && (
            <span
              style={{
                color: "#d32f2f",
                marginRight: "8px",
                fontSize: "0.85em",
                fontWeight: "bold",
                backgroundColor: "#ffebee",
                padding: "2px 6px",
                borderRadius: "4px",
              }}
            >
              OVERDUE
            </span>
          )}
          {patronAtLimit && (
            <span
              style={{
                color: "#ff6f00",
                marginRight: "8px",
                fontSize: "0.85em",
                fontWeight: "bold",
                backgroundColor: "#fff3e0",
                padding: "2px 6px",
                borderRadius: "4px",
              }}
            >
              LIMIT REACHED
            </span>
          )}
          {stats && (
            <span
              style={{
                color: "#666",
                fontSize: "0.8em",
                marginLeft: "4px",
              }}
            >
              ({stats.current}/{stats.limit} loans)
            </span>
          )}
        </div>
      </div>
    );
  };

  renderNoPendingLoans = () => {
    return (
      <Message info icon data-test="no-results">
        <Message.Content>
          <Message.Header>No loan requests</Message.Header>
          There are no loan requests for this item.
        </Message.Content>
      </Message>
    );
  };

  checkoutItemButton = ({ row }) => {
    const { performCheckoutAction, itemDetails, isPendingLoansLoading } =
      this.props;
    const { patronsWithOverdue, patronsAtLimit } = this.state;

    const itemPid = {
      type: recordToPidType(itemDetails),
      value: itemDetails.metadata.pid,
    };

    const patronPid = row.metadata.patron_pid;
    const patronHasOverdue = patronsWithOverdue.has(patronPid);
    const patronAtLimit = patronsAtLimit.has(patronPid);
    const itemCannotCirculate =
      invenioConfig.CIRCULATION.loanActiveStates.includes(
        itemDetails.metadata.circulation.state
      ) ||
      !invenioConfig.ITEMS.canCirculateStatuses.includes(
        itemDetails.metadata.status
      );

    const isDisabled =
      itemCannotCirculate ||
      isPendingLoansLoading ||
      patronHasOverdue ||
      patronAtLimit;

    let buttonColor = "green";
    let buttonText = "Checkout";
    let titleText = "Checkout item";

    if (patronHasOverdue) {
      buttonColor = "red";
      buttonText = "Overdue";
      titleText = "Cannot checkout - patron has overdue loans";
    } else if (patronAtLimit) {
      buttonColor = "orange";
      buttonText = "Limit Reached";
      titleText = "Cannot checkout - patron has reached borrowing limit";
    } else if (itemCannotCirculate) {
      titleText = "Item cannot be checked out";
    }

    return (
      <Button
        size="mini"
        color={buttonColor}
        loading={isPendingLoansLoading}
        disabled={isDisabled}
        onClick={() =>
          performCheckoutAction(
            row.availableActions.checkout,
            row.metadata.document_pid,
            row.metadata.patron_pid,
            itemPid,
            true
          )
        }
        title={titleText}
      >
        {buttonText}
      </Button>
    );
  };

  renderTable() {
    const { data, showMaxPendingLoans } = this.props;
    const columns = [
      {
        title: "Loan",
        formatter: ({ row }) => (
          <Link
            to={BackOfficeRoutes.loanDetailsFor(row.metadata.pid)}
            data-test={row.metadata.pid}
          >
            {row.metadata.pid}
          </Link>
        ),
      },
      {
        title: "Patron",
        field: "metadata.patron.name",
        formatter: this.patronDetails,
      },
      { title: "State", field: "metadata.state" },
      {
        title: "Request start date",
        field: "metadata.request_start_date",
        formatter: dateFormatter,
      },
      {
        title: "Request end date",
        field: "metadata.request_expire_date",
        formatter: dateFormatter,
      },
      {
        title: "Actions",
        field: "",
        formatter: this.checkoutItemButton,
      },
    ];

    return (
      <>
        <Header as="h3" attached="top" id="loans-request">
          Loan requests
        </Header>
        <Segment attached="bottom" className="bo-metadata-segment no-padding">
          <ResultsTable
            data={data.hits}
            columns={columns}
            totalHitsCount={data.total}
            name="loans"
            seeAllComponent={this.seeAllButton()}
            showMaxRows={showMaxPendingLoans}
            renderEmptyResultsElement={this.renderNoPendingLoans}
          />
        </Segment>
      </>
    );
  }

  render() {
    const { isLoading, error } = this.props;
    return (
      <Loader isLoading={isLoading}>
        <Error error={error}>{this.renderTable()}</Error>
      </Loader>
    );
  }
}

ItemPendingLoans.propTypes = {
  itemDetails: PropTypes.object.isRequired,
  fetchPendingLoans: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  showMaxPendingLoans: PropTypes.number,
  isLoading: PropTypes.bool,
  isPendingLoansLoading: PropTypes.bool,
  error: PropTypes.object,
  performCheckoutAction: PropTypes.func.isRequired,
};

ItemPendingLoans.defaultProps = {
  showMaxPendingLoans: 5,
  isLoading: false,
  isPendingLoansLoading: false,
  error: {},
};
